// The whole site is drawn into this 2D canvas, then pushed through the CRT shader.
// Coordinates are CSS pixels; content scrolls by `scrollY`.

import {
  FONT_PIXEL,
  FONT_TERM,
  font,
  clamp,
  lerp,
  easeOut,
  wrapText,
  drawPixelFrame,
  drawSprite,
  spriteSize,
  drawArcadeButton,
  drawChip
} from './draw.js';
import { NoiseField, PixelTrail, GlitchBlocks, TextGlitch } from './effects.js';

const TYPE_SPEED = 15; // ms per character (matches the original site)
const TYPE_DELAY = 500;
const LINE_PAUSE = 30;

export class Scene {
  constructor({ canvas, site, apps, desktop = [], reducedMotion = false, onEvent = () => {} }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false });
    this.site = site;
    this.apps = apps;
    this.desktop = desktop;
    this.reducedMotion = reducedMotion;
    this.onEvent = onEvent;

    this.W = 0;
    this.H = 0;
    this.dpr = 1;
    this.scrollY = 0;
    this.scrollTarget = 0;
    this.scrollVel = 0;
    this.contentHeight = 0;
    this.pointer = { x: -1, y: -1, type: 'none', active: false };
    this.hoverId = null;
    this.focusId = null;
    this.pressId = null;
    this.regions = [];
    this.L = null;

    this.bootAt = null;
    this.appsViewed = false;
    this.desktopViewed = false;
    this.reveal = new Map();
    this.hoverAmt = new Map();
    this.pressAmt = new Map();

    this.noise = new NoiseField();
    this.trail = new PixelTrail(12, 800);
    this.blocks = new GlitchBlocks();
    this.logoGlitch = new TextGlitch(site.name);
    this.lastFrame = 0;
  }

  /* ---------- sizing & layout ---------- */

  resize(W, H, dpr) {
    this.W = W;
    this.H = H;
    this.dpr = dpr;
    this.canvas.width = Math.floor(W * dpr);
    this.canvas.height = Math.floor(H * dpr);
    this.layout();
    this.scrollTarget = clamp(this.scrollTarget, 0, this.maxScroll());
    this.scrollY = clamp(this.scrollY, 0, this.maxScroll());
  }

  maxScroll() {
    return Math.max(0, this.contentHeight - this.H);
  }

  layout() {
    const { W, H, ctx } = this;
    const mobile = W < 700;
    const container = Math.min(W - 40, 1040);
    const cx = W / 2;
    const left = cx - container / 2;
    const regions = [];

    // HERO: logo + tagline, roughly the top 60% of the first screen
    const logoSize = clamp(W * 0.19, 56, 128);
    const tagSize = mobile ? 22 : 28;
    const tagLineH = Math.round(tagSize * 1.5);
    const groupH = logoSize * 1.2 + 48 + tagLineH * 2;
    const heroH = Math.max(groupH + 200, Math.min(H * 0.64, 760));
    const top = Math.round((heroH - groupH) / 2 + 16);
    const logo = { x: cx, y: top, size: logoSize };
    const tag = { x: cx, y: top + logoSize * 1.2 + 48, size: tagSize, lineH: tagLineH };

    // APPS
    const appsTop = heroH;
    const titleSize = mobile ? 26 : 34;
    const title = { x: cx, y: appsTop, size: titleSize };
    const subtitle = { x: cx, y: title.y + titleSize + 22, size: mobile ? 20 : 24 };
    const subtitleH = mobile ? 2 * 24 : 24;

    const cols = W >= 1000 ? 3 : W >= 680 ? 2 : 1;
    const gap = 24;
    const cardW = Math.floor((container - gap * (cols - 1)) / cols);
    const pad = 22;
    const innerW = cardW - pad * 2;
    const nameSize = cardW < 300 ? 11 : 13;
    const tagFont = font(cardW < 300 ? 20 : 22, FONT_TERM);
    const descFont = font(cardW < 300 ? 18 : 19, FONT_TERM);
    const gridTop = subtitle.y + subtitleH + 36;

    const cards = this.apps.map(app => {
      ctx.font = tagFont;
      const tagLines = wrapText(ctx, app.tagline, innerW);
      ctx.font = descFont;
      const descLines = wrapText(ctx, app.description, innerW);
      const h =
        pad + 56 + 20 + tagLines.length * 26 + 12 + descLines.length * 22 + 20 + 30 + 24 + 44 + pad;
      return { app, tagLines, descLines, h, w: cardW, pad, innerW, nameSize, tagFont, descFont };
    });

    let y = gridTop;
    for (let i = 0; i < cards.length; i += cols) {
      const row = cards.slice(i, i + cols);
      const rowH = Math.max(...row.map(c => c.h));
      row.forEach((c, j) => {
        c.x = left + j * (cardW + gap);
        c.y = y;
        c.h = rowH;
        regions.push({ id: `app-${c.app.id}`, kind: 'card', x: c.x, y: c.y, w: c.w, h: c.h });
      });
      y += rowH + gap;
    }
    const gridBottom = y - gap;

    // DESKTOP: a classic window holding the icons
    const winW = Math.min(container, 760);
    const px = mobile ? 3 : 4;
    const iconSize = 16 * px;
    const titleBarH = 30;
    const bodyPad = mobile ? 24 : 36;
    const labelSize = mobile ? 18 : 22;
    const iconCols = mobile ? 2 : this.desktop.length;
    const iconRows = Math.ceil(this.desktop.length / iconCols);
    const rowH = iconSize + 14 + labelSize;
    const rowGap = 28;
    const bodyH = bodyPad + iconRows * rowH + (iconRows - 1) * rowGap + bodyPad;
    const winTop = gridBottom + (mobile ? 64 : 96);
    const win = { x: cx - winW / 2, y: winTop, w: winW, h: titleBarH + bodyH, titleBarH };
    const colW = winW / iconCols;
    const icons = this.desktop.map((item, i) => {
      const ix = win.x + colW * (i % iconCols) + colW / 2;
      const iy = win.y + titleBarH + bodyPad + Math.floor(i / iconCols) * (rowH + rowGap);
      const hitW = Math.min(colW - 8, 180);
      const region = { id: item.id, kind: 'icon', x: ix - hitW / 2, y: iy - 10, w: hitW, h: iconSize + 14 + labelSize + 20 };
      regions.push(region);
      return { item, cx: ix, y: iy, px, size: iconSize, labelSize, labelY: iy + iconSize + 14, region };
    });

    // FOOTER
    let footerTop = win.y + win.h + (mobile ? 56 : 80);
    let sectionEnd = footerTop + 110;

    // If everything after the hero is shorter than the viewport, centre it.
    const sectionH = sectionEnd - appsTop;
    if (sectionH < H) {
      const shift = Math.floor((H - sectionH) / 2);
      title.y += shift;
      subtitle.y += shift;
      cards.forEach(c => {
        c.y += shift;
      });
      win.y += shift;
      icons.forEach(ic => {
        ic.y += shift;
        ic.labelY += shift;
      });
      regions.forEach(r => {
        if (r.kind === 'card' || r.kind === 'icon') r.y += shift;
      });
      footerTop += shift;
      sectionEnd = appsTop + H;
    }
    const footer = { x: cx, y: footerTop };
    this.contentHeight = sectionEnd;

    this.L = { mobile, container, left, cx, logo, tag, heroH, appsTop, title, subtitle, cards, win, icons, footer, cols };
    this.regions = regions;
  }

  /* ---------- input API ---------- */

  setPointer(x, y, type) {
    this.pointer.x = x;
    this.pointer.y = y;
    this.pointer.type = type;
    this.pointer.active = x >= 0 && y >= 0;
    if (this.pointer.active && type !== 'touch') this.trail.add(x, y, performance.now());
  }

  hitTest(sx, sy) {
    const x = sx;
    const y = sy + this.scrollY;
    for (const r of this.regions) {
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        return r;
      }
    }
    return null;
  }

  press(sx, sy) {
    const r = this.hitTest(sx, sy);
    this.pressId = r ? r.id : null;
  }

  release() {
    this.pressId = null;
  }

  scrollBy(dy) {
    this.scrollVel = 0;
    this.scrollTarget = clamp(this.scrollTarget + dy, 0, this.maxScroll());
  }

  scrollTo(y) {
    this.scrollVel = 0;
    this.scrollTarget = clamp(y, 0, this.maxScroll());
  }

  dragScroll(dy) {
    this.scrollTarget = clamp(this.scrollTarget + dy, 0, this.maxScroll());
    this.scrollY = this.scrollTarget;
  }

  stopScroll() {
    this.scrollVel = 0;
    this.scrollTarget = this.scrollY;
  }

  fling(v) {
    this.scrollVel = clamp(v, -4000, 4000);
  }

  setFocus(id) {
    this.focusId = id;
    const r = this.regions.find(x => x.id === id);
    if (r) this.scrollTo(r.y + r.h / 2 - this.H / 2);
  }

  /* ---------- per-frame update ---------- */

  update(now) {
    if (this.bootAt === null) this.bootAt = now;
    const dt = Math.min(64, now - (this.lastFrame || now));
    this.lastFrame = now;

    // momentum
    if (Math.abs(this.scrollVel) > 5) {
      this.scrollTarget = clamp(this.scrollTarget + (this.scrollVel * dt) / 1000, 0, this.maxScroll());
      this.scrollVel *= Math.pow(0.05, dt / 1000);
      if (this.scrollTarget === 0 || this.scrollTarget === this.maxScroll()) this.scrollVel = 0;
    } else {
      this.scrollVel = 0;
    }
    // easing
    const k = 1 - Math.exp(-dt / 70);
    this.scrollY = lerp(this.scrollY, this.scrollTarget, k);
    if (Math.abs(this.scrollY - this.scrollTarget) < 0.3) this.scrollY = this.scrollTarget;

    // hover
    const hit = this.pointer.active && this.pointer.type !== 'touch' ? this.hitTest(this.pointer.x, this.pointer.y) : null;
    this.hoverId = hit ? hit.id : null;
    for (const r of this.regions) {
      const target = this.hoverId === r.id || this.focusId === r.id ? 1 : 0;
      const cur = this.hoverAmt.get(r.id) || 0;
      this.hoverAmt.set(r.id, lerp(cur, target, 1 - Math.exp(-dt / 60)));
      const pt = this.pressId === r.id ? 1 : 0;
      const pc = this.pressAmt.get(r.id) || 0;
      this.pressAmt.set(r.id, lerp(pc, pt, 1 - Math.exp(-dt / 40)));
    }

    // card reveals
    for (const c of this.L.cards) {
      const id = c.app.id;
      if (!this.reveal.has(id) && c.y - this.scrollY < this.H * 0.92) {
        this.reveal.set(id, now + this.L.cards.indexOf(c) * 120);
        if (!this.appsViewed) {
          this.appsViewed = true;
          this.onEvent('apps_viewed');
        }
      }
    }

    for (const ic of this.L.icons) {
      const id = `icon-${ic.item.id}`;
      if (!this.reveal.has(id) && ic.y - this.scrollY < this.H * 0.92) {
        this.reveal.set(id, now + this.L.icons.indexOf(ic) * 100);
        if (!this.desktopViewed) {
          this.desktopViewed = true;
          this.onEvent('desktop_viewed');
        }
      }
    }

    this.blocks.update(now, this.W, this.H, !this.reducedMotion);
  }

  typingProgress(now) {
    const lines = this.site.tagline;
    let t = now - this.bootAt - TYPE_DELAY;
    const shown = [];
    let endAt = this.bootAt + TYPE_DELAY;
    let done = true;
    for (const line of lines) {
      const dur = line.length * TYPE_SPEED;
      endAt += dur + LINE_PAUSE;
      if (t <= 0) {
        shown.push('');
        done = false;
      } else if (t < dur) {
        shown.push(line.slice(0, Math.ceil(t / TYPE_SPEED)));
        done = false;
      } else {
        shown.push(line);
      }
      t -= dur + LINE_PAUSE;
    }
    return { shown, done, endAt };
  }

  /* ---------- drawing ---------- */

  draw(now) {
    const { ctx, W, H, dpr } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);

    this.trail.draw(ctx, now);

    ctx.save();
    ctx.translate(0, -Math.round(this.scrollY));
    this.drawHero(now);
    this.drawApps(now);
    this.drawDesktop(now);
    this.drawFooter(now);
    ctx.restore();

    this.blocks.draw(ctx);
    this.drawBurnIn(now);
    this.drawHud(now);
    this.drawScrollbar();
    if (!this.reducedMotion) this.noise.draw(ctx, W, H, 0.045);
    this.drawCursor(now);
  }

  drawHero(now) {
    const { ctx, W, H, L } = this;
    const { logo, tag } = L;

    // ambient glow behind the logo
    const gy = logo.y + logo.size * 0.6;
    const g = ctx.createRadialGradient(W / 2, gy, 0, W / 2, gy, Math.max(W, H) * 0.35);
    const pulse = 0.5 + 0.2 * Math.sin((now / 4000) * Math.PI * 2);
    g.addColorStop(0, `rgba(30,30,30,${pulse})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // logo
    const glitch = this.logoGlitch.get(now, !this.reducedMotion);
    const lp = 0.75 + 0.25 * Math.sin((now / 2000) * Math.PI * 2);
    ctx.save();
    ctx.font = font(logo.size, FONT_PIXEL);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.translate(logo.x, logo.y + glitch.jitterY);
    ctx.scale(1, 1.2);
    const textW = ctx.measureText(glitch.text).width + logo.size * 0.4;
    const textH = logo.size * 1.05;

    // idle: a faint constant chromatic fringe so the tube never looks perfectly clean
    const rgb = glitch.active ? glitch.rgb : 1.2;
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = `rgba(255,40,90,${glitch.active ? 0.9 : 0.35})`;
    ctx.fillText(glitch.text, -rgb, 0);
    ctx.fillStyle = `rgba(40,220,255,${glitch.active ? 0.9 : 0.35})`;
    ctx.fillText(glitch.text, rgb, 0);
    ctx.globalCompositeOperation = 'source-over';

    ctx.shadowColor = `rgba(255,255,255,${0.35 + 0.3 * lp})`;
    ctx.shadowBlur = 18;
    ctx.fillStyle = `rgba(255,255,255,${0.75 + 0.25 * lp})`;
    ctx.fillText(glitch.text, 0, 0);
    ctx.shadowBlur = 0;

    if (glitch.active) {
      // torn horizontal slices, each shifted and with its own channel split
      for (const sl of glitch.slices) {
        const sy = sl.y * textH;
        const sh = sl.h * textH;
        ctx.save();
        ctx.beginPath();
        ctx.rect(-W / 2, sy, W, sh);
        ctx.clip();
        ctx.fillStyle = '#000';
        ctx.fillRect(-W / 2, sy, W, sh);
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = 'rgba(255,40,90,0.9)';
        ctx.fillText(glitch.text, sl.dx - rgb * 0.6, 0);
        ctx.fillStyle = 'rgba(40,220,255,0.9)';
        ctx.fillText(glitch.text, sl.dx + rgb * 0.6, 0);
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.fillText(glitch.text, sl.dx, 0);
        ctx.restore();
      }
      // dropouts: black blocks punched out of the glyphs
      ctx.fillStyle = '#000';
      for (const d of glitch.dropouts) {
        ctx.fillRect(-textW / 2 + d.x * textW, d.y * textH, d.w * textW, d.h * textH);
      }
      // a couple of stray bright pixels
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      for (let i = 0; i < 3; i++) {
        ctx.fillRect(-textW / 2 + Math.random() * textW, Math.random() * textH, 3, 3);
      }
    }
    ctx.restore();

    // typewriter tagline
    const typing = this.typingProgress(now);
    ctx.font = font(tag.size, FONT_TERM);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#fff';
    typing.shown.forEach((line, i) => {
      const y = tag.y + i * tag.lineH;
      ctx.fillText(line, tag.x, y);
    });
    // block cursor at the end of the line being typed
    if (!typing.done || Math.floor(now / 530) % 2 === 0) {
      const i = Math.max(0, typing.shown.findIndex((l, idx) => l.length < this.site.tagline[idx].length));
      const idx = typing.done ? typing.shown.length - 1 : i;
      const line = typing.shown[idx] || '';
      const w = ctx.measureText(line).width;
      ctx.fillRect(tag.x + w / 2 + 4, tag.y + idx * tag.lineH + 4, tag.size * 0.5, tag.size * 0.9);
    }

  }

  drawApps(now) {
    const { ctx, L } = this;
    if (this.scrollY + this.H < L.appsTop - 20) return; // off screen

    // section header
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = font(L.title.size, FONT_PIXEL);
    ctx.fillStyle = '#fff';
    ctx.shadowColor = 'rgba(255,255,255,0.5)';
    ctx.shadowBlur = 14;
    ctx.fillText('APPS', L.title.x, L.title.y);
    ctx.shadowBlur = 0;

    // decorative rule either side of the title
    ctx.font = font(L.title.size, FONT_PIXEL);
    const tw = ctx.measureText('APPS').width;
    const ruleW = Math.min(180, (L.container - tw) / 2 - 40);
    if (ruleW > 30) {
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      const ry = L.title.y + L.title.size / 2 - 2;
      ctx.fillRect(L.title.x - tw / 2 - 24 - ruleW, ry, ruleW, 4);
      ctx.fillRect(L.title.x + tw / 2 + 24, ry, ruleW, 4);
    }

    ctx.font = font(L.subtitle.size, FONT_TERM);
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    const blink = Math.floor(now / 700) % 2 === 0;
    const parts = [`SELECT A CARTRIDGE ${blink ? '_' : ' '}`, 'PRODUCTS DESIGNED & SHIPPED BY UXS'];
    if (L.mobile) {
      parts.forEach((line, i) => ctx.fillText(line, L.subtitle.x, L.subtitle.y + i * (L.subtitle.size + 4)));
    } else {
      ctx.fillText(parts.join('  ·  '), L.subtitle.x, L.subtitle.y);
    }

    for (const c of L.cards) this.drawCard(c, now);
  }

  drawCard(c, now) {
    const { ctx } = this;
    const { app, pad, innerW } = c;
    const id = `app-${app.id}`;
    const revealAt = this.reveal.get(app.id);
    if (revealAt === undefined) return;
    const p = this.reducedMotion ? 1 : easeOut(clamp((now - revealAt) / 500, 0, 1));
    if (p <= 0) return;
    const hov = this.hoverAmt.get(id) || 0;
    const pressed = this.pressAmt.get(id) || 0;
    const soon = app.status === 'soon';

    ctx.save();
    ctx.globalAlpha = p;
    ctx.translate(c.x, c.y + (1 - p) * 30 - hov * 4 + pressed * 3);

    // card body
    ctx.fillStyle = `rgba(255,255,255,${(0.025 + hov * 0.03).toFixed(3)})`;
    ctx.fillRect(0, 0, c.w, c.h);
    drawPixelFrame(ctx, 0, 0, c.w, c.h, {
      color: `rgba(255,255,255,${(0.35 + 0.65 * hov).toFixed(3)})`,
      width: 3,
      notch: 8,
      dashed: soon
    });

    // cartridge number + status
    ctx.font = font(8, FONT_PIXEL);
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillText(`CART-${app.number}`, pad, pad - 4);
    ctx.textAlign = 'right';
    const status = soon ? 'COMING SOON' : 'LIVE';
    ctx.fillStyle = soon ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.8)';
    ctx.fillText(status, c.w - pad, pad - 4);
    if (!soon) {
      const sw = ctx.measureText(status).width;
      const on = Math.floor(now / 900) % 2 === 0;
      ctx.fillStyle = `rgba(255,255,255,${on ? 0.9 : 0.3})`;
      drawSprite(ctx, 'dot', c.w - pad - sw - 14, pad - 4, 2, ctx.fillStyle);
    }

    // icon + name
    let y = pad + 16;
    const px = 4;
    const ic = spriteSize(app.sprite, px);
    drawSprite(ctx, app.sprite, pad, y, px, `rgba(255,255,255,${(0.85 + 0.15 * hov).toFixed(3)})`);
    ctx.font = font(c.nameSize, FONT_PIXEL);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#fff';
    const nameX = pad + ic.w + 16;
    // selection arrow
    if (hov > 0.05) {
      const blink = Math.floor(now / 250) % 2 === 0 ? 1 : 0.4;
      ctx.save();
      ctx.globalAlpha = p * hov * blink;
      drawSprite(ctx, 'play', nameX - 2, y + ic.h / 2 - 9, 2, '#fff');
      ctx.restore();
    }
    ctx.fillText(app.name, nameX + (hov > 0.05 ? 16 * hov : 0), y + ic.h / 2);
    y += Math.max(ic.h, 40) + 20;

    // tagline
    ctx.font = c.tagFont;
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#fff';
    for (const line of c.tagLines) {
      ctx.fillText(line, pad, y);
      y += 26;
    }
    y += 12;

    // description
    ctx.font = c.descFont;
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    for (const line of c.descLines) {
      ctx.fillText(line, pad, y);
      y += 22;
    }
    y += 20;

    // tags
    let tx = pad;
    for (const t of app.tags) {
      tx += drawChip(ctx, t, tx, y, { alpha: 0.6 + 0.3 * hov }) + 8;
    }

    // launch button pinned to the bottom of the card
    const bh = 44;
    const by = c.h - pad - bh;
    const bw = Math.min(190, innerW);
    drawArcadeButton(ctx, {
      x: pad,
      y: by,
      w: bw,
      h: bh,
      label: app.cta,
      hover: hov,
      pressed,
      fontSize: 11
    });
    if (app.host) {
      ctx.font = font(18, FONT_TERM);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = `rgba(255,255,255,${(0.45 + 0.4 * hov).toFixed(3)})`;
      if (pad + bw + 12 + ctx.measureText(app.host).width < c.w - pad) {
        ctx.fillText(app.host, c.w - pad, by + bh / 2);
      }
    }

    ctx.restore();
  }

  drawDesktop(now) {
    const { ctx, L } = this;
    const { win } = L;
    if (this.scrollY + this.H < win.y - 20 || this.scrollY > win.y + win.h + 20) return;

    // window chrome
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    ctx.fillRect(win.x, win.y, win.w, win.h);
    drawPixelFrame(ctx, win.x, win.y, win.w, win.h, { color: 'rgba(255,255,255,0.6)', width: 3, notch: 6 });
    // title bar: stripes either side of the title, close box on the left
    const tb = win.titleBarH;
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillRect(win.x, win.y + tb, win.w, 3);
    ctx.font = font(8, FONT_PIXEL);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const titleText = 'UXS.EXE';
    const tw = ctx.measureText(titleText).width;
    const stripeL = win.x + 44;
    const stripeR = win.x + win.w - 16;
    for (let i = 0; i < 5; i++) {
      const sy = win.y + 8 + i * 4;
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.fillRect(stripeL, sy, L.cx - tw / 2 - 16 - stripeL, 1.5);
      ctx.fillRect(L.cx + tw / 2 + 16, sy, stripeR - (L.cx + tw / 2 + 16), 1.5);
    }
    ctx.fillStyle = '#fff';
    ctx.fillText(titleText, L.cx, win.y + tb / 2 + 1);
    // close box
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillRect(win.x + 14, win.y + 8, 16, 2);
    ctx.fillRect(win.x + 14, win.y + 22, 16, 2);
    ctx.fillRect(win.x + 14, win.y + 8, 2, 16);
    ctx.fillRect(win.x + 28, win.y + 8, 2, 16);

    // icons
    for (const ic of L.icons) {
      const id = `icon-${ic.item.id}`;
      const revealAt = this.reveal.get(id);
      if (revealAt === undefined) continue;
      const p = this.reducedMotion ? 1 : easeOut(clamp((now - revealAt) / 450, 0, 1));
      if (p <= 0) continue;
      const hov = this.hoverAmt.get(ic.item.id) || 0;
      const pressed = this.pressAmt.get(ic.item.id) || 0;
      ctx.save();
      ctx.globalAlpha = p;
      ctx.translate(0, (1 - p) * 16 + pressed * 2);

      // selection: dotted rectangle around the icon (classic desktop)
      if (hov > 0.05) {
        ctx.save();
        ctx.globalAlpha = p * hov;
        ctx.fillStyle = '#fff';
        const sx = ic.cx - ic.size / 2 - 10;
        const sy = ic.y - 10;
        const sw = ic.size + 20;
        const sh = ic.size + 20;
        for (let i = 0; i < sw; i += 6) {
          ctx.fillRect(sx + i, sy, 3, 2);
          ctx.fillRect(sx + i, sy + sh - 2, 3, 2);
        }
        for (let i = 0; i < sh; i += 6) {
          ctx.fillRect(sx, sy + i, 2, 3);
          ctx.fillRect(sx + sw - 2, sy + i, 2, 3);
        }
        ctx.restore();
      }
      drawSprite(ctx, ic.item.sprite, ic.cx - ic.size / 2, ic.y - hov * 3, ic.px, `rgba(255,255,255,${(0.85 + 0.15 * hov).toFixed(3)})`);

      // label, inverted when selected
      ctx.font = font(ic.labelSize, FONT_TERM);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const lw = ctx.measureText(ic.item.label).width;
      if (hov > 0.05) {
        ctx.fillStyle = `rgba(255,255,255,${hov.toFixed(3)})`;
        ctx.fillRect(ic.cx - lw / 2 - 6, ic.labelY - 2, lw + 12, ic.labelSize + 4);
      }
      const c = Math.round(255 * (1 - hov));
      ctx.fillStyle = `rgb(${c},${c},${c})`;
      ctx.fillText(ic.item.label, ic.cx, ic.labelY);
      ctx.restore();
    }
  }

  drawFooter(now) {
    const { ctx, L } = this;
    if (this.scrollY + this.H < L.footer.y - 20) return;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(L.left, L.footer.y - 36, L.container, 2);
    ctx.font = font(18, FONT_TERM);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fillText(this.site.copyright, L.footer.x, L.footer.y);
    ctx.font = font(8, FONT_PIXEL);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    const credits = [`${this.site.domain.toUpperCase()}  ·  CASABLANCA`, 'CRT SHADER BY GINGERBEARDMAN'];
    if (ctx.measureText(credits.join('  ·  ')).width < L.container - 40) {
      ctx.fillText(credits.join('  ·  '), L.footer.x, L.footer.y + 34);
    } else {
      credits.forEach((line, i) => ctx.fillText(line, L.footer.x, L.footer.y + 34 + i * 18));
    }
  }

  // Burn-in lives on the tube, so it never scrolls.
  drawBurnIn(now) {
    const { ctx, W, H } = this;
    const step = Math.floor(now / 5000);
    const rx = ((step * 9301 + 49297) % 233280) / 233280;
    const ry = ((step * 4297 + 9301) % 233280) / 233280;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.font = font(clamp(W * 0.12, 40, 72), FONT_PIXEL);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(255,255,255,0.035)';
    ctx.fillText(this.site.name, W / 2 + (rx * 10 - 5), H / 2 + (ry * 10 - 5));
    ctx.restore();
  }

  drawHud(now) {
    const { ctx, W, L } = this;
    ctx.save();
    if (this.scrollY > 8) {
      const g = ctx.createLinearGradient(0, 0, 0, 64);
      g.addColorStop(0, 'rgba(0,0,0,0.95)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, 64);
    }
    ctx.font = font(8, FONT_PIXEL);
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    const m = L.mobile ? 14 : 22;
    ctx.textAlign = 'left';
    ctx.fillText('1UP  UXS', m, m);
    ctx.textAlign = 'right';
    const credit = Math.floor(now / 1000) % 10 === 0 ? 'CREDIT 02' : 'CREDIT 01';
    ctx.fillText(credit, W - m, m);
    ctx.restore();
  }

  drawScrollbar() {
    const { ctx, W, H } = this;
    const max = this.maxScroll();
    if (max <= 0 || this.L.mobile) return;
    const trackTop = 56;
    const trackH = H - 112;
    const thumbH = Math.max(24, (H / this.contentHeight) * trackH);
    const thumbY = trackTop + (this.scrollY / max) * (trackH - thumbH);
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(W - 14, trackTop, 4, trackH);
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillRect(W - 14, thumbY, 4, thumbH);
  }

  drawCursor(now) {
    const { ctx, pointer } = this;
    if (!pointer.active || pointer.type === 'touch' || pointer.type === 'none') return;
    ctx.save();
    ctx.globalCompositeOperation = 'difference';
    ctx.fillStyle = '#fff';
    const x = Math.round(pointer.x);
    const y = Math.round(pointer.y);
    if (this.hoverId) {
      // hollow square + centre dot when over something clickable
      const s = 16;
      ctx.fillRect(x - s / 2, y - s / 2, s, 2);
      ctx.fillRect(x - s / 2, y + s / 2 - 2, s, 2);
      ctx.fillRect(x - s / 2, y - s / 2, 2, s);
      ctx.fillRect(x + s / 2 - 2, y - s / 2, 2, s);
      ctx.fillRect(x - 2, y - 2, 4, 4);
    } else {
      ctx.fillRect(x - 4, y - 4, 8, 8);
    }
    ctx.restore();
  }
}
